

import { useState, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { InventoryItemForm } from "./InventoryItemForm";
import { InventoryCostSummary } from "./InventoryCostSummary";
import { InventoryViewToggle } from "./InventoryViewToggle";
import { InventoryListView } from "./InventoryListView";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

interface InventoryItem {
  id: string;
  created_at: string;
  name: string;
  category: string;
  cost_per_unit: number | null;
  quantity: number | null;
  department: string | null;
  notes: string | null;
  user_id: string;
}

interface InventoryContentProps {
  selectedCategory: string | null;
  searchQuery: string;
  showItemForm: boolean;
  onCloseForm: () => void;
  setShowItemForm: (show: boolean) => void;
  sortOrder: string;
  filterCategory: string;
}

export function InventoryContent({
  selectedCategory,
  searchQuery,
  showItemForm,
  onCloseForm,
  setShowItemForm,
  sortOrder,
  filterCategory
}: InventoryContentProps) {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const queryClient = useQueryClient();

  console.log("🔍 SECURITY DEBUG - Current user:", user?.id);

  const { data: inventoryItems = [], isLoading, error, refetch } = useQuery({
    queryKey: ["inventory-items", user?.id],
    queryFn: async () => {
      if (!user) {
        console.log("🚫 SECURITY DEBUG - No user, returning empty array");
        return [];
      }

      console.log("🔍 SECURITY DEBUG - Fetching inventory for user:", user.id);
      
      const { data, error } = await supabase
        .from("inventory_items")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ SECURITY DEBUG - Error fetching inventory:", error);
        toast.error("Failed to load inventory items");
        throw error;
      }

      console.log("✅ SECURITY DEBUG - Raw data received:", {
        totalItems: data?.length || 0,
        currentUserId: user.id,
        items: data?.map(item => ({ 
          id: item.id, 
          name: item.name, 
          user_id: item.user_id,
          belongsToCurrentUser: item.user_id === user.id
        }))
      });

      // CRITICAL SECURITY CHECK: Verify all items belong to current user
      const wrongUserItems = data?.filter(item => item.user_id !== user.id) || [];
      if (wrongUserItems.length > 0) {
        console.error("🚨 SECURITY VIOLATION - Items from other users detected:", wrongUserItems);
        toast.error("Security issue detected - please refresh the page");
        // Return only items that belong to the current user
        return data?.filter(item => item.user_id === user.id) || [];
      }

      // ADDITIONAL SECURITY: Double-check every item belongs to current user
      const verifiedItems = data?.filter(item => item.user_id === user.id) || [];
      
      if (verifiedItems.length !== data?.length) {
        console.error("🚨 SECURITY VIOLATION - Item count mismatch after filtering");
        toast.error("Security violation detected - some items were filtered out");
      }

      return verifiedItems;
    },
    enabled: !!user,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!user) {
        throw new Error("User must be logged in to delete items");
      }

      console.log("🔍 DELETE DEBUG - Deleting item:", { itemId, userId: user.id });

      const { error } = await supabase
        .from("inventory_items")
        .delete()
        .eq("id", itemId)
        .eq("user_id", user.id); // Ensure user can only delete their own items

      if (error) {
        console.error("❌ DELETE DEBUG - Error deleting item:", error);
        throw error;
      }

      console.log("✅ DELETE DEBUG - Item deleted successfully");
    },
    onSuccess: () => {
      toast.success("Item deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["inventory-items", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["category-counts", user?.id] });
    },
    onError: (error) => {
      console.error("❌ DELETE DEBUG - Mutation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete item");
    },
  });

  const filteredAndSortedItems = useMemo(() => {
    let filtered = inventoryItems;

    // Apply category filter
    if (selectedCategory && selectedCategory !== "All") {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.notes?.toLowerCase().includes(query)
      );
    }

    // Apply additional category filter
    if (filterCategory !== "all") {
      filtered = filtered.filter(item => 
        item.category.toLowerCase() === filterCategory.toLowerCase()
      );
    }

    // Apply sorting
    const [sortField, sortDirection] = sortOrder.split("_");
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "category":
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        case "cost":
          aValue = a.cost_per_unit || 0;
          bValue = b.cost_per_unit || 0;
          break;
        case "quantity":
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case "date":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    console.log("🔍 SECURITY DEBUG - Final filtered items:", {
      originalCount: inventoryItems.length,
      filteredCount: filtered.length,
      selectedCategory,
      searchQuery,
      filterCategory,
      currentUserId: user?.id
    });

    return filtered;
  }, [inventoryItems, selectedCategory, searchQuery, filterCategory, sortOrder, user?.id]);

  const handleEdit = (item: InventoryItem) => {
    console.log("🔍 EDIT DEBUG - Opening edit form for item:", { id: item.id, name: item.name });
    setEditingItem(item);
    setShowItemForm(true);
  };

  const handleDelete = (itemId: string) => {
    console.log("🔍 DELETE DEBUG - Delete requested for item:", itemId);
    deleteMutation.mutate(itemId);
  };

  const handleFormClose = () => {
    setEditingItem(null);
    onCloseForm();
  };

  const handleFormSuccess = () => {
    console.log("✅ FORM DEBUG - Item saved successfully, invalidating cache");
    queryClient.invalidateQueries({ queryKey: ["inventory-items", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["category-counts", user?.id] });
    handleFormClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-xs text-muted-foreground">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <p className="text-destructive text-sm">Failed to load inventory</p>
          <button 
            onClick={() => refetch()} 
            className="mt-2 text-xs text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 w-full px-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 w-full">
        <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-white truncate">
            Items ({filteredAndSortedItems.length})
          </h2>
          {selectedCategory && selectedCategory !== "All" && (
            <Badge variant="secondary" className="bg-white/10 text-white text-xs px-2 py-0.5 flex-shrink-0">
              {selectedCategory}
            </Badge>
          )}
        </div>
        
        <div className="w-full sm:w-auto flex justify-end">
          <InventoryViewToggle 
            view={viewMode} 
            onViewChange={(view) => setViewMode(view)} 
          />
        </div>
      </div>

      <div className="w-full">
        <InventoryCostSummary items={filteredAndSortedItems} />
      </div>

      <div className="w-full">
        <InventoryListView 
          items={filteredAndSortedItems}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddItem={() => setShowItemForm(true)}
        />
      </div>

      {showItemForm && (
        <InventoryItemForm
          open={showItemForm}
          onOpenChange={(open) => {
            if (!open) {
              handleFormClose();
            }
          }}
          onSuccess={handleFormSuccess}
          editItem={editingItem}
        />
      )}
    </div>
  );
}
